import React, { useState, useEffect } from 'react';
import { DropdownCard } from '@trellixio/roaster-coffee';
import PropTypes from 'prop-types';
import FilterSelect from 'components/shared/FilterSelect';
import { preventSubmitWithENTER } from 'helpers';
import { useGetUser } from 'hooks';
import Locales from 'locales';
import { ListFilter, Loader, Search, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

// Constants for filter labels and values used in trip filtering
const CONST_NONE = 'none';
const CONST_FILTER_LABEL_AGENCY = 'agency';
const CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED = 'deposit_transferred';
const CONST_FILTER_VALUE_DEPOSIT_EMPTY = 'deposit_empty';
const CONST_FILTER_VALUE_DEPOSIT_PARTIAL = 'deposit_partial';
const CONST_FILTER_VALUE_DEPOSIT_FULL = 'deposit_full';
const CONST_FILTER_LABEL_VISA = 'visa';
const CONST_FILTER_VALUE_VISA_PENDING = 'visa_pending';
const CONST_FILTER_VALUE_VISA_SUBMITTED = 'visa_submitted';
const CONST_FILTER_LABEL_NPI = 'npi';
const CONST_FILTER_LABEL_PASSPORT = 'passport';
const CONST_FILTER_LABEL_INTERNATIONAL = 'international';
const CONST_FILTER_VALUE_INTERNATIONAL_YES = 'international_yes';
const CONST_FILTER_VALUE_INTERNATIONAL_NO = 'international_no';
const CONST_FILTER_LABEL_HEALTH = 'health';
const CONST_FILTER_VALUE_HEALTH_PENDING = 'health_pending';
const CONST_FILTER_VALUE_HEALTH_GRANTED = 'health_granted';
const CONST_FILTER_VALUE_HEALTH_REJECTED = 'health_rejected';
const CONST_FILTER_LABEL_CATEGORY = 'category';
const CONST_FILTER_LABEL_SELF_REGISTER = 'self_registered';
const CONST_FILTER_VALUE_SELF_REGISTER_YES = 'self_registration_yes';
const CONST_FILTER_VALUE_SELF_REGISTER_NO = 'self_registration_no';
const CONST_FILTER_LABEL_GROUP = 'group';
const CONST_FILTER_VALUE_GROUP_YES = 'group_yes';
const CONST_FILTER_VALUE_GROUP_NO = 'group_no';
const CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN = 'old_pilgrim_agency_ifu';
const CONST_FILTER_VALUE_ASSIGN_FROM_PREVIOUS_CAMPAIGN_YES = 'old_pilgrim_agency_ifu_yes';
const CONST_FILTER_VALUE_ASSIGN_FROM_PREVIOUS_CAMPAIGN_NO = 'old_pilgrim_agency_ifu_no';
const normalizeTripFilters = (filters) => (filters && Object.keys(filters).length > 0 ? filters : null);

export const getTripAppliedFiltersFromSearchParams = (
  searchParams,
  { isAgency = false, isMAEC = false, isDEI = false, isMS = false, isAGLO = false } = {}
) =>
  normalizeTripFilters({
    ...(!isAgency && !isDEI && searchParams.get(CONST_FILTER_LABEL_AGENCY)
      ? { [CONST_FILTER_LABEL_AGENCY]: searchParams.get(CONST_FILTER_LABEL_AGENCY) }
      : {}),
    ...(isAgency || isAGLO
      ? searchParams.get(CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED)
        ? { [CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED]: searchParams.get(CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED) }
        : {}
      : {}),
    ...(!isDEI && !isMS && searchParams.get(CONST_FILTER_LABEL_VISA)
      ? { [CONST_FILTER_LABEL_VISA]: searchParams.get(CONST_FILTER_LABEL_VISA) }
      : {}),
    ...(isAgency || isAGLO
      ? searchParams.get(CONST_FILTER_LABEL_NPI)
        ? { [CONST_FILTER_LABEL_NPI]: searchParams.get(CONST_FILTER_LABEL_NPI) }
        : {}
      : {}),
    ...(!isMS && !isMAEC && searchParams.get(CONST_FILTER_LABEL_PASSPORT)
      ? { [CONST_FILTER_LABEL_PASSPORT]: searchParams.get(CONST_FILTER_LABEL_PASSPORT) }
      : {}),
    ...(!isDEI && searchParams.get(CONST_FILTER_LABEL_INTERNATIONAL)
      ? { [CONST_FILTER_LABEL_INTERNATIONAL]: searchParams.get(CONST_FILTER_LABEL_INTERNATIONAL) }
      : {}),
    ...(!isDEI && searchParams.get(CONST_FILTER_LABEL_HEALTH)
      ? { [CONST_FILTER_LABEL_HEALTH]: searchParams.get(CONST_FILTER_LABEL_HEALTH) }
      : {}),
    ...(!isDEI && searchParams.get(CONST_FILTER_LABEL_CATEGORY)
      ? { [CONST_FILTER_LABEL_CATEGORY]: searchParams.get(CONST_FILTER_LABEL_CATEGORY) }
      : {}),
    ...(!isDEI && searchParams.get(CONST_FILTER_LABEL_SELF_REGISTER)
      ? { [CONST_FILTER_LABEL_SELF_REGISTER]: searchParams.get(CONST_FILTER_LABEL_SELF_REGISTER) }
      : {}),
    ...(!isDEI && searchParams.get(CONST_FILTER_LABEL_GROUP)
      ? { [CONST_FILTER_LABEL_GROUP]: searchParams.get(CONST_FILTER_LABEL_GROUP) }
      : {}),
    ...(isAgency || isAGLO
      ? searchParams.get(CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN)
        ? {
            [CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN]: searchParams.get(
              CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN
            ),
          }
        : {}
      : {}),
  });

const TripFilters = ({
  agencies = null,
  trips = null,
  categories = null,
  queryFilters = null,
  setQueryFilter = null,
  setSearchParams = null,
  search = null,
  setSearch = null,
  isFetchingTrips = false,
}) => {
  const { isAgency, isDEI, isMS, isAGLO } = useGetUser();

  const appliedAgency = queryFilters?.[CONST_FILTER_LABEL_AGENCY] || CONST_NONE;
  const appliedPaymentStatus = queryFilters?.[CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED] || CONST_NONE;
  const appliedVisaStatus = queryFilters?.[CONST_FILTER_LABEL_VISA] || CONST_NONE;
  const appliedNpiStatus = queryFilters?.[CONST_FILTER_LABEL_NPI] || CONST_NONE;
  const appliedPassportStatus = queryFilters?.[CONST_FILTER_LABEL_PASSPORT] || CONST_NONE;
  const appliedIsInternational = queryFilters?.[CONST_FILTER_LABEL_INTERNATIONAL] || CONST_NONE;
  const appliedMedicalVisitStatus = queryFilters?.[CONST_FILTER_LABEL_HEALTH] || CONST_NONE;
  const appliedCategory = queryFilters?.[CONST_FILTER_LABEL_CATEGORY] || CONST_NONE;
  const appliedSelfRegistered = queryFilters?.[CONST_FILTER_LABEL_SELF_REGISTER] || CONST_NONE;
  const appliedGroup = queryFilters?.[CONST_FILTER_LABEL_GROUP] || CONST_NONE;
  const appliedAssignFromPreviousCampaign =
    queryFilters?.[CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN] || CONST_NONE;

  const [selectedAgency, setAgency] = useState(appliedAgency);
  const [selectedPaymentStatus, setPaymentStatus] = useState(appliedPaymentStatus);
  const [selectedVisaStatus, setVisaStatus] = useState(appliedVisaStatus);
  const [selectedNpiStatus, setNpiStatus] = useState(appliedNpiStatus);
  const [selectedPassportStatus, setPassportStatus] = useState(appliedPassportStatus);
  const [selectedIsInternational, setIsInternational] = useState(appliedIsInternational);
  const [selectedMedicalVisitStatus, setMedicalVisitStatus] = useState(appliedMedicalVisitStatus);
  const [selectedCategory, setCategory] = useState(appliedCategory);
  const [selectedSelfRegistered, setSelfRegistered] = useState(appliedSelfRegistered);
  const [selectedGroup, setGroup] = useState(appliedGroup);
  const [selectedAssignFromPreviousCampaign, setAssignFromPreviousCampaign] = useState(
    appliedAssignFromPreviousCampaign
  );

  const resultString =
    trips?.count > 1
      ? `${trips?.count} ${Locales.trip.title.toLowerCase()}`
      : `${trips?.count || 0} ${Locales.trip.title.slice(0, -1).toLowerCase()}`;

  const selectableListDefaultOption = [{ label: Locales.base.button.select, value: CONST_NONE, disabled: true }];

  const paymentStatus = [
    { label: Locales.trip.filter.payment_statut.deposit_empty, value: CONST_FILTER_VALUE_DEPOSIT_EMPTY },
    { label: Locales.trip.filter.payment_statut.deposit_partial, value: CONST_FILTER_VALUE_DEPOSIT_PARTIAL },
    { label: Locales.trip.filter.payment_statut.deposit_full, value: CONST_FILTER_VALUE_DEPOSIT_FULL },
  ];

  const visaStatus = [
    { label: Locales.trip.filter.visa_statut.visa_pending, value: CONST_FILTER_VALUE_VISA_PENDING },
    { label: Locales.trip.filter.visa_statut.visa_submitted, value: CONST_FILTER_VALUE_VISA_SUBMITTED },
    // { label: Locales.trip.filter.visa_statut.visa_granted, value: CONST_FILTER_VALUE_VISA_GRANTED },
    // { label: Locales.trip.filter.visa_statut.visa_rejected, value: CONST_FILTER_VALUE_VISA_REJECTED },
  ];

  const internationalTrip = [
    { label: Locales.trip.filter.international_trip.international_yes, value: CONST_FILTER_VALUE_INTERNATIONAL_YES },
    { label: Locales.trip.filter.international_trip.international_no, value: CONST_FILTER_VALUE_INTERNATIONAL_NO },
  ];

  const medicalVisitStatus = [
    { label: Locales.trip.filter.medical_visit_status.health_pending, value: CONST_FILTER_VALUE_HEALTH_PENDING },
    { label: Locales.trip.filter.medical_visit_status.health_granted, value: CONST_FILTER_VALUE_HEALTH_GRANTED },
    { label: Locales.trip.filter.medical_visit_status.health_rejected, value: CONST_FILTER_VALUE_HEALTH_REJECTED },
  ];

  const selfRegistrationStatus = [
    {
      label: Locales.trip.filter.self_register_status.self_registered_yes,
      value: CONST_FILTER_VALUE_SELF_REGISTER_YES,
    },
    { label: Locales.trip.filter.self_register_status.self_registered_no, value: CONST_FILTER_VALUE_SELF_REGISTER_NO },
  ];

  const groupStatus = [
    { label: Locales.trip.filter.group_status.group_yes, value: CONST_FILTER_VALUE_GROUP_YES },
    { label: Locales.trip.filter.group_status.group_no, value: CONST_FILTER_VALUE_GROUP_NO },
  ];

  const assignFromPreviousCampaignStatus = [
    { label: Locales.trip.filter.group_status.group_yes, value: CONST_FILTER_VALUE_ASSIGN_FROM_PREVIOUS_CAMPAIGN_YES },
    { label: Locales.trip.filter.group_status.group_no, value: CONST_FILTER_VALUE_ASSIGN_FROM_PREVIOUS_CAMPAIGN_NO },
  ];

  const selectedAgencyName = () => {
    let name = null;
    if (appliedAgency !== CONST_NONE) {
      agencies?.forEach((data) => {
        if (data.id === appliedAgency) name = data.name;
      });
    }

    return name;
  };

  const selectedPaymentStatusName = () => {
    let label = null;
    if (appliedPaymentStatus !== CONST_NONE) {
      paymentStatus.forEach((data) => {
        if (data.value === appliedPaymentStatus) label = data.label;
      });
    }

    return label;
  };

  const selectedVisaStatusName = () => {
    let label = null;
    if (appliedVisaStatus !== CONST_NONE) {
      visaStatus.forEach((data) => {
        if (data.value === appliedVisaStatus) label = data.label;
      });
    }

    return label;
  };

  const selectedIsInternationalTrip = () => {
    let label = null;
    if (appliedIsInternational !== CONST_NONE) {
      internationalTrip.forEach((data) => {
        if (data.value === appliedIsInternational) label = data.label;
      });
    }

    return label;
  };

  const selectedMedicalVisitStatusName = () => {
    let label = null;
    if (appliedMedicalVisitStatus !== CONST_NONE) {
      medicalVisitStatus.forEach((data) => {
        if (data.value === appliedMedicalVisitStatus) label = data.label;
      });
    }

    return label;
  };

  const selectedCategoryName = () => {
    let label = null;
    if (appliedCategory !== CONST_NONE) {
      categories?.forEach((data) => {
        if (data.id === appliedCategory) label = data.name;
      });
    }

    return label;
  };

  const selectedSelfRegistrationStatusName = () => {
    let label = null;
    if (appliedSelfRegistered !== CONST_NONE) {
      selfRegistrationStatus.forEach((data) => {
        if (data.value === appliedSelfRegistered) label = data.label;
      });
    }

    return label;
  };

  const selectedGroupStatusName = () => {
    let label = null;
    if (appliedGroup !== CONST_NONE) {
      groupStatus.forEach((data) => {
        if (data.value === appliedGroup) label = data.label;
      });
    }

    return label;
  };

  const selectedAssignFromPreviousCampaignName = () => {
    let label = null;
    if (appliedAssignFromPreviousCampaign !== CONST_NONE) {
      assignFromPreviousCampaignStatus.forEach((data) => {
        if (data.value === appliedAssignFromPreviousCampaign) label = data.label;
      });
    }

    return label;
  };

  const filterOptions = [
    {
      id: CONST_FILTER_LABEL_AGENCY,
      label: Locales.trip.filter.agreed_agency,
      value: selectedAgency,
      data: selectableListDefaultOption.concat(agencies || []),
      onChange: setAgency,
      condition: !isAgency && !isDEI,
      tagName: selectedAgencyName(),
    },
    {
      id: CONST_FILTER_LABEL_CATEGORY,
      label: Locales.trip.filter.category,
      value: selectedCategory,
      data: selectableListDefaultOption.concat(categories || []),
      onChange: setCategory,
      condition: !isDEI,
      tagName: selectedCategoryName(),
    },
    {
      id: CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED,
      label: Locales.trip.filter.payment_status,
      value: selectedPaymentStatus,
      data: selectableListDefaultOption.concat(paymentStatus),
      onChange: setPaymentStatus,
      condition: isAgency || isAGLO,
      tagName: selectedPaymentStatusName(),
    },
    {
      id: CONST_FILTER_LABEL_HEALTH,
      label: Locales.trip.filter.medical_exam_status,
      value: selectedMedicalVisitStatus,
      data: selectableListDefaultOption.concat(medicalVisitStatus),
      onChange: setMedicalVisitStatus,
      condition: !isDEI,
      tagName: selectedMedicalVisitStatusName(),
    },
    {
      id: CONST_FILTER_LABEL_VISA,
      label: Locales.trip.filter.passport_status,
      value: selectedVisaStatus,
      data: selectableListDefaultOption.concat(visaStatus),
      onChange: setVisaStatus,
      condition: !isDEI && !isMS,
      tagName: selectedVisaStatusName(),
    },
    {
      id: CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN,
      label: Locales.trip.filter.assign_from_previous_campaign,
      value: selectedAssignFromPreviousCampaign,
      data: selectableListDefaultOption.concat(assignFromPreviousCampaignStatus),
      onChange: setAssignFromPreviousCampaign,
      condition: isAgency || isAGLO,
      tagName: selectedAssignFromPreviousCampaignName(),
    },
    {
      id: CONST_FILTER_LABEL_INTERNATIONAL,
      label: Locales.trip.filter.diaspora,
      value: selectedIsInternational,
      data: selectableListDefaultOption.concat(internationalTrip),
      onChange: setIsInternational,
      condition: !isDEI,
      tagName: selectedIsInternationalTrip(),
    },

    {
      id: CONST_FILTER_LABEL_SELF_REGISTER,
      label: Locales.trip.filter.self_register,
      value: selectedSelfRegistered,
      data: selectableListDefaultOption.concat(selfRegistrationStatus),
      onChange: setSelfRegistered,
      condition: !isDEI,
      tagName: selectedSelfRegistrationStatusName(),
    },
    {
      id: CONST_FILTER_LABEL_GROUP,
      label: Locales.trip.filter.group,
      value: selectedGroup,
      data: selectableListDefaultOption.concat(groupStatus),
      onChange: setGroup,
      condition: !isDEI,
      tagName: selectedGroupStatusName(),
    },
  ];

  // Function to apply selected filters
  const applyFilters = () => {
    setQueryFilter(
      normalizeTripFilters({
        ...(selectedAgency !== CONST_NONE ? { [CONST_FILTER_LABEL_AGENCY]: selectedAgency } : {}),
        ...(selectedPaymentStatus !== CONST_NONE
          ? { [CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED]: selectedPaymentStatus }
          : {}),
        ...(selectedVisaStatus !== CONST_NONE ? { [CONST_FILTER_LABEL_VISA]: selectedVisaStatus } : {}),
        ...(selectedNpiStatus !== CONST_NONE ? { [CONST_FILTER_LABEL_NPI]: selectedNpiStatus } : {}),
        ...(selectedPassportStatus !== CONST_NONE ? { [CONST_FILTER_LABEL_PASSPORT]: selectedPassportStatus } : {}),
        ...(selectedIsInternational !== CONST_NONE
          ? { [CONST_FILTER_LABEL_INTERNATIONAL]: selectedIsInternational }
          : {}),
        ...(selectedMedicalVisitStatus !== CONST_NONE
          ? { [CONST_FILTER_LABEL_HEALTH]: selectedMedicalVisitStatus }
          : {}),
        ...(selectedCategory !== CONST_NONE ? { [CONST_FILTER_LABEL_CATEGORY]: selectedCategory } : {}),
        ...(selectedSelfRegistered !== CONST_NONE
          ? { [CONST_FILTER_LABEL_SELF_REGISTER]: selectedSelfRegistered }
          : {}),
        ...(selectedGroup !== CONST_NONE ? { [CONST_FILTER_LABEL_GROUP]: selectedGroup } : {}),
        ...(selectedAssignFromPreviousCampaign !== CONST_NONE
          ? { [CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN]: selectedAssignFromPreviousCampaign }
          : {}),
      })
    );
  };

  // Function to reset all filters to their default state (CONST_NONE)
  const resetFilters = () => {
    setAgency(CONST_NONE);
    setPaymentStatus(CONST_NONE);
    setVisaStatus(CONST_NONE);
    setNpiStatus(CONST_NONE);
    setPassportStatus(CONST_NONE);
    setIsInternational(CONST_NONE);
    setMedicalVisitStatus(CONST_NONE);
    setCategory(CONST_NONE);
    setSelfRegistered(CONST_NONE);
    setGroup(CONST_NONE);
    setAssignFromPreviousCampaign(CONST_NONE);
    setQueryFilter(null);
  };

  // Function to remove a specific filter from the query filters
  const deleteFilter = (filter) => {
    const queryFiltersLocal = { ...queryFilters };
    delete queryFiltersLocal[filter];
    setQueryFilter(normalizeTripFilters(queryFiltersLocal));
  };

  // Function to remove a specific filter and reset its corresponding state
  const removeFilter = (filter) => {
    switch (filter) {
      case CONST_FILTER_LABEL_AGENCY:
        setAgency(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_DEPOSIT_TRANSFERRED:
        setPaymentStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_VISA:
        setVisaStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_NPI:
        setNpiStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_PASSPORT:
        setPassportStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_INTERNATIONAL:
        setIsInternational(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_HEALTH:
        setMedicalVisitStatus(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_CATEGORY:
        setCategory(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_SELF_REGISTER:
        setSelfRegistered(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_GROUP:
        setGroup(CONST_NONE);
        break;
      case CONST_FILTER_LABEL_ASSIGN_FROM_PREVIOUS_CAMPAIGN:
        setAssignFromPreviousCampaign(CONST_NONE);
        break;
      default:
        break;
    }
    deleteFilter(filter);
  };

  useEffect(() => {
    setAgency(appliedAgency);
    setPaymentStatus(appliedPaymentStatus);
    setVisaStatus(appliedVisaStatus);
    setNpiStatus(appliedNpiStatus);
    setPassportStatus(appliedPassportStatus);
    setIsInternational(appliedIsInternational);
    setMedicalVisitStatus(appliedMedicalVisitStatus);
    setCategory(appliedCategory);
    setSelfRegistered(appliedSelfRegistered);
    setGroup(appliedGroup);
    setAssignFromPreviousCampaign(appliedAssignFromPreviousCampaign);
  }, [
    appliedAgency,
    appliedAssignFromPreviousCampaign,
    appliedCategory,
    appliedGroup,
    appliedIsInternational,
    appliedMedicalVisitStatus,
    appliedNpiStatus,
    appliedPassportStatus,
    appliedPaymentStatus,
    appliedSelfRegistered,
    appliedVisaStatus,
  ]);

  useEffect(() => {
    let params = {};

    if (queryFilters) {
      params = { ...queryFilters };
    }

    if (search) {
      params = { ...params, q: search };
    }

    if (Object.keys(params).length > 0) {
      setSearchParams(params);
      return;
    }
    setSearchParams({});
  }, [queryFilters, search, setSearchParams]);

  return (
    <>
      <section className="px-4 pt-4 sm:px-5 sm:pt-5" aria-label="Recherche et filtres">
        <div className="flex items-center gap-2.5">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#7c8881]"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={Locales.trip.search}
              placeholder={Locales.trip.search}
              className="h-10 rounded-md border-[#d8e0db] bg-white pl-9 text-sm shadow-none hover:border-[#b9c6be] focus-visible:border-[#669980] focus-visible:ring-[#2c8155]/10"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={preventSubmitWithENTER}
            />
          </div>

          <DropdownCard>
            <DropdownCard.Trigger>
              <Button
                variant="outline"
                type="button"
                className={`h-10 rounded-md px-3.5 text-sm shadow-none hover:bg-[#f6f8f6] ${
                  queryFilters ? 'border-[#9db5a6] bg-[#f8fbf9] text-[#315c43]' : 'border-[#d7dfda] text-[#33463b]'
                }`}
                aria-label={
                  queryFilters
                    ? `${Locales.trip.filter.filter}, ${Object.keys(queryFilters).length} filtre(s) actif(s)`
                    : Locales.trip.filter.filter
                }
              >
                <ListFilter className="size-4" aria-hidden="true" />
                {Locales.trip.filter.filter}
                {queryFilters && <span className="size-1.5 rounded-full bg-[#6e8c79]" aria-hidden="true" />}
              </Button>
            </DropdownCard.Trigger>
            <DropdownCard.Dropdown
              width="large"
              className="!flex max-h-[min(596px,calc(100vh-32px))] !w-[368px] max-w-[calc(100vw-24px)] flex-col overflow-hidden !rounded-[9px] !border-[#dbe4dd] !bg-white !p-0 shadow-[0_8px_30px_rgba(29,57,34,0.1)]"
            >
              <div className="flex items-center border-b border-[#e5ebe7] px-4 py-3">
                <h3 className="text-sm font-semibold text-[#2b3d31]">Filtres</h3>
              </div>
              <DropdownCard.Content className="min-h-0 overflow-y-auto overscroll-contain !px-4 !py-3 [scrollbar-color:#c2cec5_transparent] [scrollbar-width:thin]">
                {filterOptions.map((option) =>
                  option.condition ? (
                    <FilterSelect
                      key={option.id}
                      label={option.label}
                      value={option.value}
                      data={option.data}
                      onChange={option.onChange}
                    />
                  ) : null
                )}
              </DropdownCard.Content>
              <DropdownCard.Actions className="!flex !shrink-0 !items-center !justify-between !border-t !border-[#e5ebe7] !bg-white !px-4 !py-3">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-9 px-3 text-sm text-[#526158]"
                  onClick={resetFilters}
                >
                  {Locales.base.button.reset}
                </Button>
                <Button type="button" variant="hajj" className="h-9 px-3 text-sm shadow-none" onClick={applyFilters}>
                  {Locales.base.button.validate}
                </Button>
              </DropdownCard.Actions>
            </DropdownCard.Dropdown>
          </DropdownCard>
        </div>
      </section>

      <div className="flex min-h-12 flex-wrap items-center gap-2 px-4 py-2.5 text-xs text-[#657269] sm:px-5">
        {isFetchingTrips && (
          <Loader className="size-4 animate-spin text-[#2c8155]" role="status" aria-label="Chargement" />
        )}
        <span aria-live="polite" className="whitespace-nowrap">
          {Locales.base.result} : <strong className="font-semibold text-[#27382e]">{resultString}</strong>
        </span>

        {queryFilters && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5" aria-label="Filtres actifs">
            {filterOptions.map(
              (filter) =>
                queryFilters[filter.id] && (
                  <span
                    key={filter.id}
                    className="inline-flex min-h-7 max-w-full items-center rounded-md border border-[#dce4df] bg-[#f6f8f7] pl-2.5 text-xs text-[#53655a]"
                  >
                    <span className="min-w-0 py-1">
                      {filter.label} :{' '}
                      <strong className="font-semibold text-[#3c5143]">
                        {filter.tagName || 'Valeur sélectionnée'}
                      </strong>
                    </span>
                    <button
                      type="button"
                      className="ml-1 inline-flex size-7 shrink-0 items-center justify-center rounded text-[#77877c] hover:bg-[#e7ede9] hover:text-[#3b5344] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c8155]"
                      aria-label={`Retirer le filtre ${filter.label}`}
                      onClick={() => removeFilter(filter.id)}
                    >
                      <X className="size-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )
            )}
          </div>
        )}
      </div>
    </>
  );
};

TripFilters.propTypes = {
  agencies: PropTypes.arrayOf(PropTypes.shape({})),
  trips: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  queryFilters: PropTypes.shape({}),
  setQueryFilter: PropTypes.func,
  setSearchParams: PropTypes.func,
  search: PropTypes.string,
  setSearch: PropTypes.func,
  isFetchingTrips: PropTypes.bool,
};

export default TripFilters;
